import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';

// Device-local storage key for the "remember this workspace" alias.
// localStorage (not a cookie): the backend cookie was HttpOnly, which
// JavaScript can never read, so cookie-based pre-fill could not work.
// The alias is a workspace slug, not a credential, so JS-readable
// storage is acceptable. Persists across browser restarts, no expiry.
const REMEMBERED_ALIAS_KEY = 'last_workspace_alias';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [alias, setAlias] = useState('');
  const [rememberAlias, setRememberAlias] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Pre-fill the workspace alias if the user previously opted in.
  useEffect(() => {
    const remembered = localStorage.getItem(REMEMBERED_ALIAS_KEY);
    if (remembered) setAlias(remembered);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!alias.trim()) {
      setError('Please enter your workspace alias.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        alias: alias.trim(),
        email,
        password,
        remember_alias: rememberAlias,
      }, { withCredentials: true });

      // Persist the alias device-locally only after a successful login,
      // honoring the checkbox. Unchecking clears any remembered alias.
      if (rememberAlias) {
        localStorage.setItem(REMEMBERED_ALIAS_KEY, alias.trim());
      } else {
        localStorage.removeItem(REMEMBERED_ALIAS_KEY);
      }

      const token = response.data.access_token;
      const tenant = response.data.tenant;
      const userWithTenant = {
        ...response.data.user,
        company_id: tenant?.id,
        company_name: tenant?.name || ''
      };

      // Flow: Agreement Acceptance → Password Change → Dashboard
      if (response.data.requires_agreement_acceptance) {
        localStorage.setItem('temp_token', token);
        localStorage.setItem('temp_user', JSON.stringify(userWithTenant));
        localStorage.setItem('temp_tenant', JSON.stringify(tenant));
        localStorage.setItem('temp_requires_password_change',
          response.data.requires_password_change ? 'true' : 'false');
        navigate('/accept-agreement');
      } else if (response.data.requires_password_change) {
        localStorage.setItem('temp_token', token);
        localStorage.setItem('temp_user', JSON.stringify(userWithTenant));
        localStorage.setItem('temp_tenant', JSON.stringify(tenant));
        navigate('/change-password');
      } else {
        onLogin(userWithTenant, token);
        authLogin(token, userWithTenant, tenant);
        navigate('/');
      }
    } catch (err) {
      // Server returns a generic "Invalid credentials" for any failure
      // (unknown alias, unknown email, wrong password). Keep the message
      // generic on the client too — never reveal which field is wrong.
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4" data-testid="login-page">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img
              src="/ergoniq-logo.svg"
              alt="Ergoniq Logo"
              className="h-20 w-auto"
            />
          </div>
          <p className="text-[#1B5C94] font-semibold">Safety | Energy | Resources | Sustainability</p>
        </div>

        <form onSubmit={handleSubmit} data-testid="login-form">
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="alias">
              Workspace alias <span className="text-red-500">*</span>
            </label>
            <input
              data-testid="alias-input"
              id="alias"
              type="text"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
              placeholder="your-workspace"
              autoComplete="organization"
              autoCapitalize="none"
              spellCheck={false}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Forgot your workspace? Contact your administrator.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="email">
              Email
            </label>
            <input
              data-testid="email-input"
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="password">
              Password
            </label>
            <input
              data-testid="password-input"
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
              required
            />
            <div className="text-right mt-2">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-[#1B5C94] hover:text-[#3AC0DB] font-semibold transition-colors"
              >
                Forgot Password?
              </button>
            </div>
          </div>

          <div className="mb-6 flex items-center">
            <input
              data-testid="remember-alias-checkbox"
              id="rememberAlias"
              type="checkbox"
              checked={rememberAlias}
              onChange={(e) => setRememberAlias(e.target.checked)}
              className="w-4 h-4 text-[#1B5C94] border-gray-300 rounded focus:ring-[#1B5C94]"
            />
            <label htmlFor="rememberAlias" className="ml-2 text-sm text-gray-700 select-none">
              Remember this workspace on this device
            </label>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm" data-testid="error-message">
              {error}
            </div>
          )}

          <button
            data-testid="login-button"
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white font-bold py-3 px-4 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Logging in...' : 'LOGIN'}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Need access? Contact your administrator or</p>
          <a href="mailto:support@saarv.in" className="text-[#1B5C94] hover:text-[#3AC0DB] font-semibold">
            support@saarv.in
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
